import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGetScalingSize } from '@ui/Report';
import { ConsumptionItem } from '../ReportOperationalEnergy.model';
import { ColorShades } from '@shared/helpers/color.helpers';

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  pieChart: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    aspectRatio: '1/1',
    position: 'relative',
  },
  pieChartSvg: {
    width: '100%',
    height: '100%',
    transform: 'rotate(-90deg)', // Start from top
  },
  pieSegment: {},
  emptyPlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.secondary',
    // Use the report scaling unit so the label keeps the same proportion in
    // sidebar thumbnails and on the full page (absolute `rem` made the label
    // dominate the thumbnail tile).
    fontSize: getScalingSize(1.1),
  },
}));

type PieChartProps = {
  data?: ConsumptionItem[];
  colorShades: ColorShades;
};

export const PieChart: React.FC<PieChartProps> = ({
  data = [],
  colorShades,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize });

  // Filter out items with zero or negative values
  const chartData = React.useMemo(() => {
    return data.filter((item) => item.value > 0);
  }, [data]);

  // Calculate pie chart segments
  const segments = React.useMemo(() => {
    if (chartData.length === 0) return [];

    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = 0;

    return chartData.map((item) => {
      const percentage = total > 0 ? (item.value / total) * 100 : 0;
      const angle = (percentage / 100) * 360;

      // Calculate SVG path for pie segment
      const radius = 50;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;

      const startX = 50 + radius * Math.cos((startAngle * Math.PI) / 180);
      const startY = 50 + radius * Math.sin((startAngle * Math.PI) / 180);
      const endX = 50 + radius * Math.cos((endAngle * Math.PI) / 180);
      const endY = 50 + radius * Math.sin((endAngle * Math.PI) / 180);

      const largeArcFlag = angle > 180 ? 1 : 0;

      const path = [
        `M 50 50`,
        `L ${startX} ${startY}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        'Z',
      ].join(' ');

      currentAngle += angle;

      return {
        ...item,
        path,
        percentage,
        startAngle,
        endAngle,
      };
    });
  }, [chartData]);

  if (segments.length === 0) {
    return (
      <Box className={classes.pieChart}>
        <Box className={classes.emptyPlaceholder}>No data</Box>
      </Box>
    );
  }

  return (
    <Box className={classes.pieChart}>
      <svg
        className={classes.pieChartSvg}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
      >
        {segments.map((segment) => (
          <path
            key={segment.consumer}
            d={segment.path}
            fill={colorShades.getById(segment.consumer)}
            stroke="white"
            strokeWidth="1"
            strokeLinejoin="round"
            className={classes.pieSegment}
          />
        ))}
      </svg>
    </Box>
  );
};
